import { apiGet, apiMutation, apiMutationPreservingSession } from './client';
import { normalizeAdminMfaStatus } from './admin-mfa-status';

export type AdminDataScopeType =
  | 'global'
  | 'region'
  | 'tenant'
  | 'organization'
  | 'assigned'
  | 'self'
  | 'masked_readonly';

export interface AdminDataScope {
  type: AdminDataScopeType;
  value?: string;
  tenantId?: string;
  organizationId?: string;
  regionCode?: string;
}

export interface AdminAccessSnapshot {
  administratorId: string;
  principalId: string | null;
  principalStatus: string | null;
  displayName: string | null;
  role: string;
  roleCodes: string[];
  tenantId: string | null;
  mfaRequired: boolean;
  mfaVerified: boolean;
  permissionSource: 'super_admin' | 'assigned' | string;
  effectivePermissions: string[];
  dataScopes: AdminDataScope[];
}

export interface AdminPermissionCatalogEntry {
  code: string;
  label: string;
  domain: string;
  domainLabel: string;
  resource: string;
  action: string;
  risk: 'R0' | 'R1' | 'R2' | 'R3' | 'R4';
  allowedDataScopes: AdminDataScopeType[];
}

export interface AdminPermissionCatalog {
  version: string;
  riskLevels: Array<{ code: string; label: string }>;
  permissions: AdminPermissionCatalogEntry[];
}

export const fetchCurrentAdminAccess = () => apiGet<AdminAccessSnapshot>('/admin/me/permissions');
export const fetchAdminPermissionCatalog = () => apiGet<AdminPermissionCatalog>('/admin/permissions/catalog');

export interface AdminPrincipalSummary {
  id: string; status: string; tenantId?: string | null; mfaRequired: boolean; version: number;
  activeSessionCount: number; createdAt: string; allowedStatusTransitions: string[];
  user: { id: string; email: string; phone?: string | null; displayName?: string | null; avatarUrl?: string | null; role: string };
  roleAssignments: Array<{ id: string; role: { code: string; name: string } }>;
}

export interface AdminPrincipalPage { items: AdminPrincipalSummary[]; total: number; page: number; limit: number }
export interface AdminUserCandidate {
  id: string;
  displayName: string | null;
  phone: string | null;
  email: string | null;
  avatarUrl: string | null;
  administrator: { id: string; status: string; statusLabel: string } | null;
}
export interface AdminRoleSummary {
  id: string; code: string; name: string; description?: string | null; level: number; enabled: boolean;
  tenantScoped: boolean; system: boolean; version: number;
  permissions: Array<{ permissionCode: string }>;
  _count?: { assignments: number };
}
export interface AdminAccessRequestSummary {
  id: string; requesterPrincipalId: string; targetPrincipalId: string; requestType: string; status: string;
  sourceAccessRequestId?: string | null;
  requestedPermissionCode?: string | null; scopeType: string; scopeValue?: string | null; reason: string;
  requiredApprovals: number; firstApprovedByPrincipalId?: string | null; secondApprovedByPrincipalId?: string | null;
  expiresAt?: string | null; version: number; createdAt: string;
  requester?: { user: { id: string; displayName?: string | null; email: string } };
  target?: { user: { id: string; displayName?: string | null; email: string } };
  requestedRole?: { id: string; code: string; name: string } | null;
}
export interface AdminSecurityEventSummary { id: string; eventType: string; severity: string; resourceType: string; resourceId: string; reason: string; occurredAt: string }
export interface AdminSessionSummary {
  id: string;
  status: string;
  deviceName?: string | null;
  browserName?: string | null;
  operatingSystem?: string | null;
  ipAddress?: string | null;
  authenticatedAt?: string | null;
  mfaVerifiedAt?: string | null;
  lastSeenAt: string;
  expiresAt: string;
  revokedAt?: string | null;
  revokeReason?: string | null;
  version: number;
}
export interface AdminPrincipalIdentity {
  principalId: string;
  userId: string | null;
  displayName: string | null;
  email: string | null;
}
export interface AdminEffectivePermissionGrantSource {
  id: string;
  grantKind: 'ROLE' | 'DIRECT';
  source: 'DIRECT' | 'ROLE' | 'TEMPORARY' | 'EMERGENCY' | 'TENANT' | 'LEGACY_COMPATIBILITY';
  temporary: boolean;
  role: { id: string; code: string; name: string } | null;
  permissionCodes: string[];
  reason: string;
  scope: AdminDataScope;
  grantedBy: AdminPrincipalIdentity | null;
  startsAt: string;
  expiresAt: string | null;
  accessRequest: {
    id: string;
    requestType: string;
    status: string;
    reason: string;
    requester: AdminPrincipalIdentity | null;
    approvers: AdminPrincipalIdentity[];
    effectiveAt: string | null;
    expiresAt: string | null;
  } | null;
}
export interface AdminEffectivePermissions {
  permissionCodes: string[];
  roleCodes: string[];
  dataScopes: AdminDataScope[];
  grantSources: AdminEffectivePermissionGrantSource[];
}
export interface AdminMfaStatus {
  principalId: string;
  required: boolean; configured: boolean; principalVersion: number;
  method: { id: string; methodType: string; label: string; status: string; verifiedAt?: string | null; revokedAt?: string | null; version: number } | null;
  currentSession: { verifiedAt?: string | null; version: number };
}
export interface AdminMfaEnrollment {
  methodId: string; status: 'PENDING'; version: number; secret: string; otpAuthUri: string; qrCodeDataUrl: string;
}
export interface GovernedAction { actionId: string; reason: string; expectedVersion: number }
export interface AdminSuperAdminBootstrapIdentity {
  displayName: string | null;
  phone: string | null;
  email: string | null;
}
export interface AdminSuperAdminBootstrapRequest {
  id: string;
  initiatedByPrincipalId: string;
  targetPrincipalId: string;
  reason: string;
  status: 'PENDING_TARGET_CONFIRMATION' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
  expiresAt: string;
  acceptedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  version: number;
  initiator?: AdminSuperAdminBootstrapIdentity | null;
  target?: AdminSuperAdminBootstrapIdentity | null;
}
export interface AdminSuperAdminBootstrapStatus {
  builtInRoleReady: boolean;
  effectiveSuperAdminCount: number;
  bootstrapCompleted: boolean;
  eligible: boolean;
  blockerCode:
    | 'BUILT_IN_ROLE_MISSING'
    | 'NO_EFFECTIVE_SUPER_ADMIN'
    | 'ALREADY_INITIALIZED'
    | 'NOT_SOLE_SUPER_ADMIN'
    | 'MFA_REQUIRED'
    | 'SESSION_MFA_REQUIRED'
    | 'PENDING_REQUEST_EXISTS'
    | null;
  currentPrincipalId: string;
  currentActorIsTarget: boolean;
  currentActorIsInitiator: boolean;
  mfaConfigured: boolean;
  sessionMfaFresh: boolean;
  version: number;
  pendingRequest: AdminSuperAdminBootstrapRequest | null;
}

export const fetchAdminUsers = (query = '') => apiGet<AdminPrincipalPage>(`/admin/admin-users${query ? `?${query}` : ''}`);
export const searchAdminUserCandidates = (q: string, limit = 20) => apiGet<{ items: AdminUserCandidate[] }>(
  `/admin/admin-user-candidates?q=${encodeURIComponent(q)}&limit=${limit}`,
);
export const fetchAdminUser = (id: string) => apiGet<AdminPrincipalSummary>(`/admin/admin-users/${id}`);
export const fetchAdminUserEffectivePermissions = (id: string) => apiGet<AdminEffectivePermissions>(`/admin/admin-users/${id}/effective-permissions`);
export const createAdminUser = (body: GovernedAction & { userId: string; tenantId?: string; mfaRequired?: boolean }) => apiMutation<{ id: string; status: string; version: number }>('/admin/admin-users', 'POST', body);
export const updateAdminUserStatus = (id: string, body: GovernedAction & { status: string }) => apiMutation(`/admin/admin-users/${id}/status`, 'PATCH', body);
export const fetchAdminRoles = () => apiGet<AdminRoleSummary[]>('/admin/roles');
export const createAdminRole = (body: GovernedAction & { code: string; name: string; description?: string; level: number; tenantScoped: boolean; permissionCodes: string[] }) => apiMutation('/admin/roles', 'POST', body);
export const updateAdminRole = (id: string, body: GovernedAction & { name?: string; description?: string; enabled?: boolean; permissionCodes?: string[] }) => apiMutation(`/admin/roles/${id}`, 'PATCH', body);
export const fetchAdminAccessRequests = () => apiGet<AdminAccessRequestSummary[]>('/admin/access-requests');
export const fetchAdminSecurityEvents = () => apiGet<AdminSecurityEventSummary[]>('/admin/security-events');
export const createAdminAccessRequest = (body: GovernedAction & Record<string, unknown>) => apiMutation('/admin/access-requests', 'POST', body);
export const approveAdminAccessRequest = (id: string, body: GovernedAction) => apiMutation(`/admin/access-requests/${id}/approve`, 'POST', body);
export const returnAdminAccessRequest = (id: string, body: GovernedAction) => apiMutation(`/admin/access-requests/${id}/return`, 'POST', body);
export const resubmitAdminAccessRequest = (id: string, body: GovernedAction) => apiMutation(`/admin/access-requests/${id}/resubmit`, 'POST', body);
export const rejectAdminAccessRequest = (id: string, body: GovernedAction) => apiMutation(`/admin/access-requests/${id}/reject`, 'POST', body);
export const executeAdminAccessRequest = (id: string, body: GovernedAction) => apiMutation(`/admin/access-requests/${id}/execute`, 'POST', body);
export const fetchAdminSessions = (principalId: string) => apiGet<AdminSessionSummary[]>(`/admin/admin-users/${principalId}/sessions`);
export const fetchMyAdminSessions = () => apiGet<AdminSessionSummary[]>('/admin/me/sessions');
export const revokeAdminSession = (sessionId: string, body: GovernedAction) => apiMutation(`/admin/sessions/${sessionId}/revoke`, 'POST', body);
export const revokeMyAdminSession = (sessionId: string, body: GovernedAction) => apiMutation(`/admin/me/sessions/${sessionId}/revoke`, 'POST', body);
export const revokeAllAdminSessions = (principalId: string, body: GovernedAction) => apiMutation(`/admin/admin-users/${principalId}/sessions/revoke-all`, 'POST', body);
export const fetchAdminMfaStatus = async () => normalizeAdminMfaStatus(await apiGet<AdminMfaStatus>('/admin/mfa/status'));
export const enrollAdminMfa = (body: GovernedAction & { currentPassword: string; label: string }) => apiMutationPreservingSession<AdminMfaEnrollment>('/admin/mfa/totp/enroll', 'POST', body);
export const activateAdminMfa = (body: GovernedAction & { code: string }) => apiMutationPreservingSession('/admin/mfa/totp/activate', 'POST', body);
export const verifyAdminMfa = (body: GovernedAction & { code: string }) => apiMutationPreservingSession('/admin/mfa/verify', 'POST', body);
export const fetchSuperAdminBootstrapStatus = () =>
  apiGet<AdminSuperAdminBootstrapStatus>('/admin/super-admin-bootstrap/status');
export const createAdminReauthenticationTicket = (body: {
  currentPassword: string;
  purpose: 'INITIAL_SUPER_ADMIN_BOOTSTRAP' | 'ADMIN_MFA_RESET_EXECUTION';
}) => apiMutationPreservingSession<{ ticket: string; expiresAt: string }>('/admin/reauthentication/tickets', 'POST', body);
export const initiateAdminSuperAdminBootstrap = (
  body: GovernedAction & { targetPrincipalId: string; reauthenticationTicket: string },
) => apiMutation<AdminSuperAdminBootstrapRequest>('/admin/super-admin-bootstrap/requests', 'POST', body);
export const acceptAdminSuperAdminBootstrap = (
  id: string,
  body: GovernedAction & { reauthenticationTicket: string },
) => apiMutation<AdminSuperAdminBootstrapRequest>(`/admin/super-admin-bootstrap/requests/${id}/accept`, 'POST', body);
export const cancelAdminSuperAdminBootstrap = (id: string, body: GovernedAction) =>
  apiMutation<AdminSuperAdminBootstrapRequest>(`/admin/super-admin-bootstrap/requests/${id}/cancel`, 'POST', body);
